import ThumbImg from "../media/ThumbImg";
import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import ReportModal from "../modals/ReportModal";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { CornerUpLeft, MessageSquare, Phone } from "lucide-react";
import { readableNameColor } from "../../lib/nameColor";
import { formatShortTime } from "../../lib/time";
import { MessageActionSheet, MessageDetailsSheet } from "./MessageSheets";
import type { Msg, LinkPreview, ReactionInfo } from "../../types/chat";

const EmojiPanel = lazy(() => import("../media/EmojiPanel"));

type SeenTip = { x: number; y: number; ids: string[] } | null;
type HoverReaction = { messageId: string; emoji: string } | null;
type AttachmentItem = NonNullable<Msg["attachments"]>[number];

type MemberLite = { id: string; name?: string; username?: string; avatarUrl?: string | null; status?: string | null; nameColor?: string | null };

type MessageListVirtualizedProps = {
  messages: Msg[];
  markId: string | null;
  cid: string;
  scrollParent?: HTMLElement | null;
  isMine: (m: Msg) => boolean;
  pickerFor: string | null;
  setPickerFor: (id: string | null) => void;
  reactionBtnRef: React.RefObject<HTMLButtonElement | null>;
  reactionMenuRef: React.RefObject<HTMLDivElement | null>;
  setReplyTo: (m: Msg | null) => void;
  startEdit: (m: Msg) => void;
  editingId: string | null;
  editText: string;
  setEditText: (v: string) => void;
  saveEdit: () => void;
  cancelEdit: () => void;
  toggleReaction: (m: Msg, emoji: string) => void;
  revealedSpoilers: Record<string, boolean>;
  setRevealedSpoilers: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setImagePreview: (preview: { url: string; name?: string } | null) => void;
  setImagePreviewList: (list: { url: string; name?: string }[]) => void;
  setImagePreviewIndex: (idx: number) => void;
  setVideoPreview: (preview: { url: string; name?: string } | null) => void;
  linkifyMessage: (text: string, key: string) => { nodes: React.ReactNode; urls: string[] };
  linkPreviewMap: Record<string, LinkPreview | null | undefined>;
  renderLinkPreview: (preview: LinkPreview, key: string) => React.ReactNode;
  members: MemberLite[];
  hoverReaction: HoverReaction;
  setHoverReaction: React.Dispatch<React.SetStateAction<HoverReaction>>;
  reactionTipPos: { x: number; y: number } | null;
  setReactionTipPos: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  reactionInfoByMsg: Record<string, Record<string, { users: ReactionInfo[] }>>;
  loadReactionsFor: (messageId: string) => void;
  seenTip: SeenTip;
  setSeenTip: React.Dispatch<React.SetStateAction<SeenTip>>;
  timeAgo: (d?: string) => string;
  formatTime: (d?: string) => string;
  onDeleteMessage: (id: string) => void | Promise<void>;
  quickEmojis: string[];
  scrollToId?: string | null;
  highlightMsgId?: string | null;
};

export function MessageListVirtualized({
  messages,
  markId,
  cid,
  scrollParent,
  isMine,
  pickerFor,
  setPickerFor,
  reactionBtnRef,
  reactionMenuRef,
  setReplyTo,
  startEdit,
  editingId,
  editText,
  setEditText,
  saveEdit,
  cancelEdit,
  toggleReaction,
  revealedSpoilers,
  setRevealedSpoilers,
  setImagePreview,
  setImagePreviewList,
  setImagePreviewIndex,
  setVideoPreview,
  linkifyMessage,
  linkPreviewMap,
  renderLinkPreview,
  members,
  hoverReaction,
  setHoverReaction,
  setReactionTipPos,
  reactionInfoByMsg,
  loadReactionsFor,
  seenTip,
  setSeenTip,
  formatTime,
  onDeleteMessage,
  quickEmojis,
  scrollToId,
  highlightMsgId,
}: MessageListVirtualizedProps) {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const [reportTarget, setReportTarget] = useState<{ type: 'message' | 'user'; id: string; label: string } | null>(null);

  // Touch interactions: press and hold opens the action sheet, a single tap opens the details sheet.
  const [sheet, setSheet] = useState<{ mode: 'actions' | 'details'; id: string } | null>(null);
  const press = useRef<{ timer: number | null; x: number; y: number; firedAt: number; pointerType: string }>({ timer: null, x: 0, y: 0, firedAt: 0, pointerType: '' });
  const LONG_PRESS_MS = 420;
  const PRESS_MOVE_TOLERANCE = 10;
  const cancelPress = () => {
    if (press.current.timer !== null) { window.clearTimeout(press.current.timer); press.current.timer = null; }
  };
  useEffect(() => cancelPress, []);
  const sheetMsg = sheet ? messages.find(mm => mm.id === sheet.id) || null : null;
  useEffect(() => {
    if (sheet?.mode === 'details') loadReactionsFor(sheet.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet?.mode, sheet?.id]);
  const touchHandlers = (m: Msg) => ({
    onPointerDown: (e: React.PointerEvent) => {
      press.current.pointerType = e.pointerType;
      if (e.pointerType !== 'touch' || editingId === m.id || m.optimistic) return;
      if ((e.target as HTMLElement).closest('textarea,input')) return;
      cancelPress();
      press.current.x = e.clientX;
      press.current.y = e.clientY;
      press.current.timer = window.setTimeout(() => {
        press.current.timer = null;
        press.current.firedAt = Date.now();
        try { navigator.vibrate?.(10); } catch { /* haptics unavailable */ }
        setSheet({ mode: 'actions', id: m.id });
      }, LONG_PRESS_MS);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (press.current.timer === null) return;
      if (Math.abs(e.clientX - press.current.x) > PRESS_MOVE_TOLERANCE || Math.abs(e.clientY - press.current.y) > PRESS_MOVE_TOLERANCE) cancelPress();
    },
    onPointerUp: cancelPress,
    onPointerCancel: cancelPress,
    // Android raises a context menu on long press; the sheet replaces it.
    onContextMenu: (e: React.MouseEvent) => { if (press.current.pointerType === 'touch') e.preventDefault(); },
    onClick: (e: React.MouseEvent) => {
      if (press.current.pointerType !== 'touch') return;
      if (Date.now() - press.current.firedAt < 700) { e.preventDefault(); e.stopPropagation(); return; }
      if (editingId === m.id || m.optimistic) return;
      if ((e.target as HTMLElement).closest('a,button,input,textarea,video,[role="button"]')) return;
      setSheet({ mode: 'details', id: m.id });
    },
  });

  useEffect(() => {
    if (!scrollToId) return;
    const index = messages.findIndex(m => m.id === scrollToId);
    if (index === -1) return;
    requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex({ index, align: 'start', behavior: 'auto' });
    });
  }, [scrollToId, messages]);

  const lastIdx = messages.length - 1;

  const isImageAttachment = (a: AttachmentItem) => {
    const label = `${a.name || ""} ${a.url || ""}`;
    return !!a.contentType?.startsWith("image/") || /\.(png|jpe?g|gif|webp|avif|svg)(?:[?#].*)?$/i.test(label);
  };
  const isVideoAttachment = (a: AttachmentItem) => {
    const label = `${a.name || ""} ${a.url || ""}`;
    return !!a.contentType?.startsWith("video/") || /\.(mp4|webm|mov|m4v)(?:[?#].*)?$/i.test(label);
  };

  const renderMessage = (m: Msg, idx: number) => {
    const byIds = Array.isArray(m.seenByIds) ? m.seenByIds : [];
    const showSeen = idx === lastIdx && byIds.length > 0;
    const showNew = markId && m.id === markId;

    // Date separator — show when this message is on a different calendar day than the previous
    const prevMsg = idx > 0 ? messages[idx - 1] : null;
    const showDateSep = (() => {
      if (!m.createdAt) return false;
      if (!prevMsg?.createdAt) return idx === 0;
      return new Date(m.createdAt).toDateString() !== new Date(prevMsg.createdAt).toDateString();
    })();
    const dateSepLabel = m.createdAt
      ? new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(m.createdAt))
      : '';

    // Author
    const member = members.find(mm => String(mm.id) === String(m.authorId));
    const avatarUrl = member?.avatarUrl || null;
    const authorColor = (() => {
      if (isMine(m)) {
        try {
          const u = JSON.parse(localStorage.getItem('user') || 'null');
          if (u?.nameColor) return String(u.nameColor);
          const alt = localStorage.getItem('nameColor');
          if (alt) return String(alt);
        } catch { /* noop */ }
      }
      return m.authorColor ? String(m.authorColor) : undefined;
    })();
    const readableColor = readableNameColor(authorColor);
    const authorLabel = isMine(m) ? 'You' : (m.authorName || 'User');
    const initials = (authorLabel[0] || '?').toUpperCase();
    const spoilerHidden = !!m.spoiler && !revealedSpoilers[m.id];

    // Consecutive messages from one author within five minutes read as one block: header and avatar only on the first.
    const GROUP_WINDOW_MS = 5 * 60 * 1000;
    const grouped = !!prevMsg && prevMsg.kind !== 'call_event' && !showDateSep && !showNew && !m.replyTo
      && String(prevMsg.authorId) === String(m.authorId)
      && !!m.createdAt && !!prevMsg.createdAt
      && new Date(m.createdAt).getTime() - new Date(prevMsg.createdAt).getTime() < GROUP_WINDOW_MS;

    const dateSeparator = showDateSep && dateSepLabel ? (
      <div className="flex items-center gap-3 px-4 py-3 select-none">
        <div className="h-px flex-1 bg-white/10" />
        <span className="text-xs font-medium text-fg-3 whitespace-nowrap">{dateSepLabel}</span>
        <div className="h-px flex-1 bg-white/10" />
      </div>
    ) : null;

    const newMarker = showNew ? (
      <div className="sticky top-0 z-10 my-1 flex items-center gap-2 text-meta text-emerald-300 px-4">
        <div className="flex-1 h-px bg-emerald-800/60" />
        <span>New</span>
        <div className="flex-1 h-px bg-emerald-800/60" />
      </div>
    ) : null;

    if (m.kind === "call_event") {
      return (
        <div className="pb-1 pt-1" data-mid={m.id}>
          {dateSeparator}
          {newMarker}
          <div className="flex items-center justify-center gap-3 py-1 text-xs text-fg-2 px-4">
            <div className="h-px max-w-16 flex-1 bg-white/10" />
            <span className="flex items-center gap-2 rounded-full bg-white/[0.05] px-3 py-1.5">
              <Phone className="h-3.5 w-3.5 text-fg-3" strokeWidth={1.5} />
              <span>{m.content}</span>
              <span className="text-fg-3">{formatTime(m.createdAt)}</span>
            </span>
            <div className="h-px max-w-16 flex-1 bg-white/10" />
          </div>
        </div>
      );
    }

    return (
      <div className="group/message" data-mid={m.id}>
        {dateSeparator}
        {newMarker}
        {/* Message row — transparent, Discord-style */}
        <div {...touchHandlers(m)} className={`relative group flex items-start gap-3 px-4 [@media(hover:none)]:select-none [-webkit-touch-callout:none] ${grouped ? 'py-0.5' : 'pt-2 pb-0.5'} hover:bg-white/[0.03] transition-colors ${m.optimistic ? 'opacity-60' : ''}`}>
          {/* Avatar, or a time gutter on grouped rows */}
          {grouped ? (
            <div className="w-9 shrink-0 pt-0.5 text-right text-meta text-fg-3 opacity-0 transition-opacity group-hover:opacity-100" title={formatTime(m.createdAt)}>
              {new Date(m.createdAt || 0).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(/\s?[AP]M$/i, '')}
            </div>
          ) : (
            <div className="shrink-0 mt-0.5 h-9 w-9 rounded-full overflow-hidden bg-white/10 flex items-center justify-center">
              {avatarUrl
                ? <ThumbImg w={56} src={avatarUrl} alt={authorLabel} className="h-full w-full object-cover" />
                : <span className="text-xs font-medium text-fg-2">{initials}</span>
              }
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Name + timestamp (first message of a group only) */}
            {!grouped && (
              <div className="flex items-baseline gap-2 mb-0.5">
                <span className="text-sm font-medium leading-tight" style={readableColor ? { color: readableColor } : undefined}>
                  {authorLabel}
                </span>
                <span className="text-meta text-fg-3" title={formatTime(m.createdAt)}>{formatShortTime(m.createdAt)}</span>
                {m.updatedAt && <span className="text-meta text-fg-3">edited</span>}
              </div>
            )}

            {/* Reply preview */}
            {m.replyTo && (
              <div className="mb-1.5 flex items-start gap-1.5 border-l-2 border-white/20 pl-2 text-xs">
                <CornerUpLeft className="mt-0.5 h-3 w-3 shrink-0 text-fg-3" strokeWidth={1.5} aria-hidden="true" />
                <span className="font-medium text-fg-2">{m.replyTo.authorName || 'User'}</span>
                <span className="line-clamp-1 text-fg-3">{m.replyTo.content || ''}</span>
              </div>
            )}

            {/* Attachments */}
            {m.attachments && m.attachments.length > 0 && (
              <div className="mt-1 relative">
                <div className="flex flex-wrap gap-2 select-none">
                  {m.attachments.map((a, i) => (
                    <div key={i} className="relative rounded-xl overflow-hidden bg-neutral-950/50 max-w-full">
                      {isImageAttachment(a) ? (
                        <button type="button" disabled={spoilerHidden}
                          onClick={() => {
                            if (spoilerHidden) return;
                            const imageAttachments = m.attachments!.filter(isImageAttachment);
                            const clickedIdx = imageAttachments.indexOf(a);
                            setImagePreviewList(imageAttachments.map(att => ({ url: att.url, name: att.name })));
                            setImagePreviewIndex(clickedIdx >= 0 ? clickedIdx : 0);
                            setImagePreview({ url: a.url, name: a.name });
                          }}
                          className="block bg-transparent p-0 m-0 cursor-zoom-in">
                          <ThumbImg w={320} src={a.url} alt={a.name || 'image'}
                            className={`max-h-64 max-w-xs rounded-xl object-cover ${spoilerHidden ? 'blur-md brightness-50' : ''}`}
                            loading="lazy" />
                        </button>
                      ) : isVideoAttachment(a) ? (
                        <button type="button" disabled={spoilerHidden}
                          onClick={() => { if (!spoilerHidden) setVideoPreview({ url: a.url, name: a.name }); }}
                          className="relative block bg-transparent p-0 m-0 cursor-pointer">
                          <video src={a.url}
                            className={`max-h-64 max-w-xs rounded object-cover bg-black ${spoilerHidden ? 'blur-md brightness-50' : ''}`}
                            muted playsInline preload="metadata"
                            onLoadedMetadata={(e) => { const v = e.currentTarget; if (Number.isFinite(v.duration) && v.duration > 0) v.currentTime = Math.min(0.1, v.duration / 2); }} />
                          {!spoilerHidden && (
                            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/40 bg-black/55 text-white shadow-lg">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="ml-0.5 h-5 w-5"><path d="M8 5v14l11-7z" /></svg>
                              </span>
                            </span>
                          )}
                        </button>
                      ) : (
                        <a href={spoilerHidden ? undefined : a.url} target={spoilerHidden ? undefined : "_blank"} rel="noreferrer"
                          className="px-2 py-1 inline-flex items-center gap-2 text-sm text-emerald-300 hover:underline max-w-full break-words">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0"><path d="M21.44 11.05l-8.49 8.49a5 5 0 0 1-7.07-7.07l8.49-8.49a3.5 3.5 0 0 1 4.95 4.95l-8.49 8.49a2 2 0 1 1-2.83-2.83l8.49-8.49"/></svg>
                          <span className="break-normal">{a.name || 'file'}</span>
                        </a>
                      )}
                    </div>
                  ))}
                </div>
                {spoilerHidden && (
                  <button className="absolute inset-0 flex items-center justify-center bg-neutral-950/90 text-fg text-sm font-medium rounded"
                    onClick={() => setRevealedSpoilers(prev => ({ ...prev, [m.id]: true }))}>
                    Spoiler — tap to reveal
                  </button>
                )}
              </div>
            )}

            {/* Message text */}
            <div className="whitespace-pre-wrap break-normal text-fg leading-relaxed">
              {editingId === m.id ? (
                <div>
                  <textarea value={editText} onChange={e => setEditText(e.target.value)}
                    className="w-full p-2 rounded bg-neutral-950 border border-neutral-800 text-sm"
                    rows={2} spellCheck autoCorrect="on" autoCapitalize="sentences" />
                  <div className="flex gap-2 mt-1 text-xs">
                    <button className="text-emerald-400 hover:underline" onClick={saveEdit}>Save</button>
                    <button className="text-fg-3 hover:underline" onClick={cancelEdit}>Cancel</button>
                  </div>
                </div>
              ) : spoilerHidden ? (
                <button className="px-2 py-1 rounded border border-neutral-800 bg-neutral-950/70 text-fg-2 text-sm"
                  onClick={() => setRevealedSpoilers(prev => ({ ...prev, [m.id]: true }))}>
                  Spoiler — click to reveal
                </button>
              ) : (
                (() => {
                  const { nodes, urls } = linkifyMessage(m.content || '', `msg-${m.id}`);
                  const uniqueUrls = Array.from(new Set(urls));
                  return (
                    <>
                      {nodes}
                      {m.optimistic ? <span className="text-fg-3"> …</span> : null}
                      {uniqueUrls.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {uniqueUrls.map((url, i) => renderLinkPreview(linkPreviewMap[url] ?? { url }, `${m.id}-preview-${i}`))}
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>

            {/* Reactions */}
            {Object.keys(m.reactions || {}).length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                {Object.entries(m.reactions || {}).map(([emoji, v]) => (
                  <div key={emoji} className="relative inline-block">
                    <button
                      onClick={() => toggleReaction(m, emoji)}
                      onMouseEnter={(e) => {
                        setHoverReaction({ messageId: m.id, emoji });
                        loadReactionsFor(m.id);
                        try { const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect(); setReactionTipPos({ x: Math.round(r.left + r.width / 2), y: Math.round(r.top) }); } catch { /* noop */ }
                      }}
                      onMouseLeave={() => { setHoverReaction(hr => (hr?.messageId === m.id && hr.emoji === emoji ? null : hr)); setReactionTipPos(null); }}
                      className={`px-2 py-0.5 text-sm rounded-full transition-colors ${(v as any).mine ? 'bg-brand/20' : 'bg-white/5 hover:bg-white/10'}`}
                    >
                      {emoji} <span className="text-xs text-fg-2 ml-0.5">{(v as any).count}</span>
                    </button>
                    {hoverReaction?.messageId === m.id && hoverReaction.emoji === emoji && (
                      <div className="absolute bottom-full left-0 mb-1 z-20 pointer-events-none">
                        <div className="rounded-md border border-neutral-700 bg-neutral-900 text-meta shadow-xl p-1.5 max-w-[12rem]">
                          {(() => {
                            const info = reactionInfoByMsg[m.id]?.[emoji]?.users || [];
                            if (!info.length) return <div className="text-fg-3">No details</div>;
                            return (
                              <div className="space-y-0.5 max-h-48 overflow-auto">
                                {info.map((it, i) => {
                                  const who = it.name?.trim() || it.username?.trim() || it.userId?.slice(0, 8) || 'User';
                                  const mem = members.find(mm => mm.id === it.userId);
                                  return (
                                    <div key={i} className="flex items-center justify-between gap-2">
                                      <span className="truncate max-w-[18ch]" style={readableNameColor(mem?.nameColor) ? { color: readableNameColor(mem?.nameColor) } : undefined}>{who}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Seen by */}
            {showSeen && (
              <div className="mt-1 relative inline-flex">
                <div className="flex gap-1 cursor-default"
                  onMouseEnter={(e) => { try { const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); setSeenTip({ x: Math.round(r.left), y: Math.round(r.top), ids: byIds }); } catch { /* noop */ } }}
                  onMouseLeave={() => setSeenTip(null)}>
                  {byIds.slice(-8).map(uid => {
                    const mem = members.find(mm => mm.id === uid);
                    const src = mem?.avatarUrl || '';
                    const title = mem?.name || mem?.username || 'User';
                    return (
                      <div key={uid} className="h-4 w-4 rounded-full overflow-hidden border border-neutral-800 -mr-1" title={`Seen by ${title}`}>
                        {src ? <img src={src} alt={title} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-neutral-700 text-[0.5rem] flex items-center justify-center text-fg">{(title[0] || '?').toUpperCase()}</div>}
                      </div>
                    );
                  })}
                </div>
                {seenTip && (
                  <div
                    className="fixed z-[30000] pointer-events-none rounded-md border border-neutral-700 bg-neutral-900 shadow-xl p-2 w-max max-w-[180px]"
                    style={{
                      left: Math.min(seenTip.x, Math.max(8, (typeof window !== 'undefined' ? window.innerWidth : 400) - 196)),
                      top: seenTip.y,
                      transform: 'translateY(calc(-100% - 6px))',
                    }}
                  >
                    <div className="text-meta text-fg-3 mb-1">Seen by</div>
                    <ul className="space-y-1">
                      {seenTip.ids.map(uid => {
                        const mem = members.find(mm => mm.id === uid);
                        const name = mem?.name || mem?.username || uid.slice(0, 8);
                        const src = mem?.avatarUrl || '';
                        return (
                          <li key={uid} className="flex items-center gap-2 text-sm text-fg">
                            <div className="h-5 w-5 rounded-full overflow-hidden border border-neutral-700 shrink-0">
                              {src ? <img src={src} alt={name} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-neutral-700 text-[0.5625rem] flex items-center justify-center">{(name[0] || '?').toUpperCase()}</div>}
                            </div>
                            <span className="truncate">{name}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Hover action bar */}
          {editingId !== m.id && (
            <div className="pointer-events-auto hidden group-hover:flex group-focus-within:flex [@media(hover:none)]:!hidden items-center gap-0.5 absolute top-1 right-3 z-40 rounded-lg border border-neutral-800 bg-neutral-900/95 px-1 py-0.5 shadow-lg">

              <button ref={pickerFor === m.id ? reactionBtnRef : undefined}
                className="p-1.5 rounded hover:bg-neutral-800 text-fg-2 hover:text-white"
                onClick={() => setPickerFor(pickerFor === m.id ? null : m.id)} aria-label="React" title="React">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><circle cx="12" cy="12" r="9"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><path d="M9 9h.01M15 9h.01"/></svg>
              </button>
              <button className="p-1.5 rounded hover:bg-neutral-800 text-fg-2 hover:text-white"
                onClick={() => setReplyTo(m)} aria-label="Reply" title="Reply">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M10 19l-7-7 7-7"/><path d="M3 12h12a6 6 0 0 1 6 6v3"/></svg>
              </button>
              {isMine(m) && (
                <button className="p-1.5 rounded hover:bg-neutral-800 text-fg-2 hover:text-white"
                  onClick={() => startEdit(m)} aria-label="Edit" title="Edit">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"/></svg>
                </button>
              )}
              {isMine(m) && (
                <button className="p-1.5 rounded hover:bg-neutral-800 text-fg-2 hover:text-red-300"
                  onClick={() => onDeleteMessage(m.id)} aria-label="Delete" title="Delete">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                </button>
              )}
              {!isMine(m) && (
                <button className="p-1.5 rounded hover:bg-neutral-800 text-fg-2 hover:text-orange-300"
                  onClick={() => setReportTarget({ type: 'message', id: m.id, label: m.content?.slice(0, 60) || 'message' })}
                  aria-label="Report message" title="Report message">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                </button>
              )}
            </div>
          )}

          {/* Emoji picker */}
          {editingId !== m.id && pickerFor === m.id && (
            <Suspense fallback={null}>
              <EmojiPanel
                quickEmojis={quickEmojis}
                onSelect={(native: string) => { if (native) toggleReaction(m, native); }}
                onClose={() => setPickerFor(null)}
                panelRef={reactionMenuRef}
              />
            </Suspense>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
    <Virtuoso
      ref={virtuosoRef}
      data={messages}
      style={{ height: "100%" }}
      customScrollParent={scrollParent || undefined}
      overscan={400}
      computeItemKey={(index: number, item: Msg) => item.id || `${cid}-${index}`}
      itemContent={(index: number, item: Msg) => (
        <div
          data-mid={item.id}
          style={highlightMsgId === item.id ? { backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 8, transition: 'background-color 0.3s' } : undefined}
        >
          {renderMessage(item, index)}
        </div>
      )}
      components={{
        EmptyPlaceholder: () => (
          <div className="flex flex-col items-start gap-3 px-6 py-10">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.05] text-fg-2">
              <MessageSquare className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <div>
              <p className="text-base font-medium text-fg">Nothing here yet</p>
              <p className="mt-1 text-sm text-fg-2">Messages sent in this channel show up here.</p>
            </div>
          </div>
        ),
      }}
    />
    {sheetMsg && sheet?.mode === 'actions' && (
      <MessageActionSheet
        msg={sheetMsg}
        mine={isMine(sheetMsg)}
        quickEmojis={quickEmojis}
        onReact={(emoji) => toggleReaction(sheetMsg, emoji)}
        onMoreReactions={() => setPickerFor(sheetMsg.id)}
        onReply={() => setReplyTo(sheetMsg)}
        onEdit={() => startEdit(sheetMsg)}
        onDelete={() => { void onDeleteMessage(sheetMsg.id); }}
        onReport={() => setReportTarget({ type: 'message', id: sheetMsg.id, label: sheetMsg.content?.slice(0, 60) || 'message' })}
        onClose={() => setSheet(null)}
      />
    )}
    {sheetMsg && sheet?.mode === 'details' && (
      <MessageDetailsSheet
        msg={sheetMsg}
        authorLabel={isMine(sheetMsg) ? 'you' : (sheetMsg.authorName || 'User')}
        members={members}
        reactionUsers={reactionInfoByMsg[sheetMsg.id]}
        onClose={() => setSheet(null)}
      />
    )}
    {reportTarget && (
      <ReportModal
        targetType={reportTarget.type}
        targetId={reportTarget.id}
        targetLabel={reportTarget.label}
        onClose={() => setReportTarget(null)}
      />
    )}
    </>
  );
}
